import type { TourTaskState } from "../api/types";
import type { InternalTourTask } from "./types";

export function combineBulkTasks(
  tasks: InternalTourTask[],
): InternalTourTask[] {
  const { bulkGroups, individualTasks } = separateBulkAndIndividualTasks(tasks);

  const groupedTasks: InternalTourTask[] = [];

  bulkGroups.forEach((bulkTasks) => {
    groupedTasks.push(createBulkGroup(bulkTasks));
  });

  groupedTasks.push(...individualTasks);

  return groupedTasks;
}

function separateBulkAndIndividualTasks(tasks: InternalTourTask[]) {
  const bulkGroups = new Map<string, InternalTourTask[]>();
  const individualTasks: InternalTourTask[] = [];

  tasks.forEach((task) => {
    if (task.bulkInfo && task.type === "order-delivery") {
      const bulkId = task.bulkInfo.id;

      if (!bulkGroups.has(bulkId)) {
        bulkGroups.set(bulkId, []);
      }

      bulkGroups.get(bulkId)!.push(task);
    } else {
      individualTasks.push(task);
    }
  });

  return { bulkGroups, individualTasks };
}

function createBulkGroup(bulkTasks: InternalTourTask[]): InternalTourTask {
  if (bulkTasks.length === 1) {
    return bulkTasks[0];
  }

  const firstTask = bulkTasks[0];
  const totalPrice = bulkTasks.reduce((sum, task) => sum + task.price, 0);
  const combinedState = determineBulkTaskState(bulkTasks);

  return {
    type: firstTask.type,
    id: `bulk-${firstTask.bulkInfo!.id}`,
    parentId: firstTask.bulkInfo!.id,
    price: totalPrice,
    name: firstTask.bulkInfo!.name,
    state: combinedState,
    note: firstTask.note,
    pharmacyName: firstTask.pharmacyName,
    bulkInfo: firstTask.bulkInfo,
    bulkTasks,
    isBulkGroup: true,
    phoneNumber: firstTask.phoneNumber,
    mobileNumber: firstTask.mobileNumber,
    isUrgent: bulkTasks.some((task) => task.isUrgent),
    cooling: firstTask.cooling,
    isBTM: bulkTasks.some((task) => task.isBTM),
    jobNr: firstTask.jobNr,
  };
}

export function determineBulkTaskState(tasks: any[]): TourTaskState {
  const states = tasks.map((task) => task.state);

  if (states.includes("failed")) {
    return "failed";
  }

  if (states.every((state) => state === "canceled")) {
    return "canceled";
  }

  if (states.every((state) => state === "done")) {
    return "done";
  }

  if (states.includes("loaded") || states.includes("nextUp")) {
    return "nextUp";
  }

  if (states.some((state) => state === "done")) {
    return "nextUp";
  }

  if (
    states.includes("canceled") &&
    !states.every((state) => state === "canceled")
  ) {
    return "nextUp";
  }

  if (states.every((state) => state === "scheduled" || state === "assigned")) {
    return "scheduled";
  }

  return "scheduled";
}
